// Sugalarm — bulletproof control floor (cycle 63; cycle 73).
//
// The cycle-38/61/62 chain that drives the rich banner / overlay UI
// has three independent break points, any of which could remove the
// Stop / Snooze control from the screen while audio kept looping:
//
//   (1) Edge-trigger miss — engine writes one alert row at the
//       level transition and nothing after; a device that connected
//       mid-low had no `phase` to drive the banner.
//   (2) Local recovery flap — one in-range reading ended the
//       episode and yanked the control; re-entry needed an
//       edge-trigger the server wouldn't re-emit.
//   (3) `lastAlert` level mismatch — the cycle-61 exact-level
//       adoption broke when the reading oscillated across the low
//       ↔ urgent_low boundary.
//
// Cycle 63 closes the gap two ways: a server-side sustained-alarm
// heartbeat (see `alerts_service._heartbeat_sustained`) and this
// **frontend safety net**. The hook (`useAlarmEpisode`) exposes a
// derived `alarmActive` predicate that is true whenever alarm audio
// is looping OR the current live reading sits in an alarming zone
// and is not stale. `alarmActive` does NOT depend on `phase`,
// `alertId`, or `socket.lastAlert.level`.
//
// This guard renders **iff `alarmActive` is true AND the primary
// banner/overlay would render no control of its own** (i.e. when
// `episode.phase === "idle"` — the in-alarm-zone-but-no-episode
// case). When the rich banners are mounted, they own the
// presentation; this guard yields. So we never double-render two
// controls for the same episode.
//
// Cycle 73: per-zone control contract — **urgent_low loses Stop**
// even on this safety floor. Hypoglycemic events must be actively
// addressed; the guard must never offer Stop while the live reading
// sits in urgent_low. To preserve the cycle-72/73 invariant ("at
// least one control whenever audio could be sounding") the guard
// renders Snooze in that case. Every other alarming level keeps
// the cycle-63 Stop + Snooze pair. The buttons wire to `stopNow()`
// and `address()` on the hook — `stopNow()` is the guaranteed
// superset of cycle-62 `stop()` that works even when no alert id
// is captured; for urgent_low, `useAlarmEpisode.stop*()` is also a
// defense-in-depth no-op so a stray caller can't bypass the gating
// here.

import type { AlarmEpisodeState } from "../../hooks/useAlarmEpisode";
import { ALARM_BANNER_ROW_PX, ALARM_BUTTON_MIN_PX } from "./alarmBannerStyle";

interface Props {
  episode: AlarmEpisodeState;
  /** Override for tests / preview. Defaults to the hook-derived
   *  phase-live predicate `episode.isLive`. */
  active?: boolean;
}

export function AlarmSilenceGuard({ episode, active }: Props) {
  // Cycle 81: the controls floor now gates on the phase-live
  // predicate (`episode.isLive`) instead of `alarmActive` (the live
  // reading's zone). `alarmActive` went false the instant the reading
  // climbed back across its threshold mid-episode (the recovery-dwell
  // window), yanking this last-resort control even while the loop
  // could still sound. Binding to `isLive` makes the floor track the
  // episode, not the reading zone — and because `audioOn ⇒ phase ===
  // "active" ⇒ isLive`, the floor is present for the whole live
  // episode, dwell included. It renders NOTHING when the episode is
  // not phase-live (recovered / idle).
  const live = active ?? episode.isLive;
  // Stay mutually exclusive with the dedicated rich surfaces so we
  // never paint two control bars for one episode: the floor yields
  // whenever a banner / inline view already owns the control for this
  // episode's level. `low` → <LowAlarmBanner>, `high` →
  // <HighAlarmBanner>, a collapsed urgent → <UrgentBanner>; an active
  // urgent is handled by NowScreen's inline full-screen view before
  // the floor is ever reached. The floor is the residual safety net —
  // "a control for any live episode that lacks one on its own
  // surface."
  // Cycle 187: a collapsed urgent now includes the **Stopped** state
  // (not just Snoozed), matching <UrgentBanner>'s widened gate — so
  // when a Stopped urgent_high paints the banner, this floor yields
  // instead of double-rendering a second control. (`stopped` is only
  // ever true for `urgent_high` — the cycle-73 `urgent_low` floor.)
  // Cycle 454: urgent high is covered by <HighAlarmBanner> in EVERY state
  // (it is no longer a takeover and never collapses to <UrgentBanner>).
  const coveredByDedicatedSurface =
    episode.episodeLevel === "low" ||
    episode.episodeLevel === "high" ||
    episode.episodeLevel === "urgent_high" ||
    (episode.episodeLevel === "urgent_low" &&
      (episode.hasSnoozed ||
        episode.phase === "snoozed" ||
        episode.stopped ||
        // Cycle 391: a collapsed TREATING urgent is likewise owned by
        // <UrgentBanner>, which renders its own Snooze in that state —
        // so the floor yields instead of painting a second control bar.
        // Widened in lockstep with the banner's own gate; if one moves
        // without the other, this episode either double-renders a
        // control or loses one.
        episode.treating));
  // Cycle 304: also yield when the episode is back in range on a fresh
  // reading (the stand-down predicate). Cycle 398 widened that predicate to
  // ACTIVE episodes, so the safety argument is restated here in the form
  // that now carries it:
  //
  //   `audioOn` is DERIVED from `bannerSuppressedInRange`
  //   (`audioOn = active && !silenced && !treating && !standDown`), so
  //   `standDown ⇒ ¬audioOn` HOLDS BY CONSTRUCTION.
  //
  // The cycle-81 floor says "a control on screen whenever the loop could be
  // sounding". When this yields, the loop cannot be sounding — in the SAME
  // render, not one render later. That is the same proposition cycle 304
  // guaranteed from the other side (`audioOn ⇒ ¬standDown`); both are
  // `¬(audioOn ∧ standDown)`. The floor is re-derived, not weakened.
  //
  // A reading going alarm-side again flips the predicate false and the
  // floor/banner AND the audio return in the same render.
  const shouldRender =
    live && !coveredByDedicatedSurface && !episode.bannerSuppressedInRange;
  if (!shouldRender) return null;

  // Cycle 454 (operator decision): High and Urgent High are Stop-only, so
  // the floor never offers them Snooze (it is not reached for them anyway —
  // <HighAlarmBanner> covers both — but the floor must not contradict it).
  const canSnooze =
    episode.episodeLevel === "low" ||
    episode.episodeLevel === "urgent_low";
  // Cycle 73: urgent_low does NOT offer Stop on the safety floor.
  // Snooze becomes the only control in that case, preserving the
  // "at least one control whenever the loop could be sounding"
  // invariant. canSnooze is already true for urgent_low (set above)
  // so we just hide Stop and let Snooze stand alone.
  const showStop = episode.episodeLevel !== "urgent_low";

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        flexShrink: 0,
        // Cycle 452.2 (size only): 52 px to carry its 44 px buttons.
        height: ALARM_BANNER_ROW_PX,
        padding: "0 16px",
        // Cycle-36.2 safe-area top inset so the band on iOS doesn't
        // hide behind the status bar when it sits flush at the top.
        paddingTop: "env(safe-area-inset-top, 0px)",
        background: "var(--zone-urgent)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: "inherit",
        fontWeight: 700,
        fontSize: 14,
        letterSpacing: "0.02em",
        boxShadow: "0 1px 0 rgba(0, 0, 0, 0.18)",
        // Above normal Now-screen chrome but below the full-bleed
        // urgent overlay (which is z-index 100) — the guard is the
        // fallback, not a competitor.
        position: "relative",
        zIndex: 60,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: "#fff",
          flexShrink: 0,
          animation: "pulseDot 1.1s ease-in-out infinite",
        }}
      />
      <span style={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>
        Alarm — controls floor
      </span>
      <span style={{ flex: 1 }} />
      {showStop && (
        <button type="button" onClick={episode.stopNow} style={btnStyle}>
          Stop
        </button>
      )}
      {canSnooze && (
        <button
          type="button"
          onClick={() => void episode.address()}
          style={showStop ? secondaryBtnStyle : btnStyle}
        >
          Snooze
        </button>
      )}
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  appearance: "none",
  border: 0,
  background: "#fff",
  color: "#8a1a1a",
  borderRadius: 999,
  padding: "6px 16px",
  fontWeight: 800,
  fontSize: 13,
  letterSpacing: "0.04em",
  cursor: "pointer",
  textTransform: "uppercase",
  // Cycle 452.2 (size only): 44 px, the alarm-button floor (was 32).
  minHeight: ALARM_BUTTON_MIN_PX,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.18)",
  fontFamily: "inherit",
};

// Cycle 63: visually distinct from Stop so a panicked-glance tap
// doesn't hit the wrong control. Outlined-white-on-red mirrors the
// cycle-62 UrgentOverlay secondary button.
const secondaryBtnStyle: React.CSSProperties = {
  appearance: "none",
  border: "1.5px solid #fff",
  background: "transparent",
  color: "#fff",
  borderRadius: 999,
  padding: "5px 14px",
  fontWeight: 700,
  fontSize: "var(--type-floor)",
  letterSpacing: "0.06em",
  cursor: "pointer",
  textTransform: "uppercase",
  minHeight: ALARM_BUTTON_MIN_PX,
  fontFamily: "inherit",
};
